import { CalendarIcon } from 'assets/icons';
import { USDate } from 'Components/Dates/USDate';
import CancelSelectionButton from 'Components/Form/SearchDropDown/CancelSelectionButton';
import { useCreatePopup } from 'Components/Popups';
import { PopupHandles } from 'Components/Popups/Popup/type';
import { ReactNode, useEffect, useState } from 'react';

import { SingleDatePicker } from './SingleDatePicker';

const getValidDate = (d: string | Date) => {
  const date = new Date(d);
  if (isNaN(date.getTime())) {
    return new Date('');
  }
  return date;
};
export interface SingleDateInputProps {
  value?: Date | string;
  minDate?: Date | string;
  maxDate?: Date | string;
  disabled?: boolean;
  onChange?: (d?: Date) => void;
  onChangeStringDate?: (d?: string) => void;
  clearable?: boolean;
  className?: string;
  showIcon?: boolean;
  border?: string;
  hourOffset?: 'min' | 'max';
  placeholder?: ReactNode;
  format?: 'UTC' | 'LOCAL';
}
export function SingleDateInput({
  value,
  onChange,
  onChangeStringDate,
  minDate: _minDate,
  maxDate: _maxDate,
  disabled = false,
  clearable = true,
  showIcon = true,
  border,
  hourOffset = 'min',
  placeholder,
  format = 'LOCAL',
  className,
}: SingleDateInputProps) {
  const Popup = useCreatePopup<Date | undefined>();
  const [date, setDate] = useState<Date>();
  useEffect(() => {
    if (value) {
      const date = getValidDate(value);
      setDate(date);
      Popup.setContextData(date);
    } else {
      setDate(undefined);
      Popup.setContextData(undefined);
    }
  }, [value]);

  const onChangeHandler = (newDate?: Date) => {
    if (!newDate) {
      onChange && onChange(undefined);
      onChangeStringDate && onChangeStringDate(undefined);
      return;
    }
    onChange && onChange(newDate);
    onChangeStringDate &&
      onChangeStringDate(
        format === 'UTC'
          ? newDate.toISOString()
          : `${newDate.getFullYear()}/${
              newDate.getMonth() + 1
            }/${newDate.getDate()}`,
      );
    Popup.setContextData(newDate);
    Popup.close();
  };
  const minDate = _minDate ? new Date(_minDate) : undefined;
  const maxDate = _maxDate ? new Date(_maxDate) : undefined;
  return (
    <>
      <DateInputButton
        disabled={disabled}
        Popup={Popup}
        onClear={onChangeHandler}
        value={date}
        clearable={clearable}
        showIcon={showIcon}
        className={className}
        border={border}
        placeholder={placeholder}
      />
      {!disabled && (
        <Popup.Component
          placement="bottom"
          matchAnchorWidth={true}
          minWidth={278}
        >
          {({ data: date }) => (
            <SingleDatePicker
              value={date}
              onChange={onChangeHandler}
              maxDate={maxDate}
              hourOffset={hourOffset}
              minDate={minDate}
            />
          )}
        </Popup.Component>
      )}
    </>
  );
}

function DateInputButton({
  value,
  onClear,
  Popup,
  clearable = true,
  disabled = false,
  showIcon = true,
  border = 'rounded-xl border border-gray-100 focus:shadow',
  className = 'h-10 py-2 ',
  placeholder = 'MM/DD/YYYY',
}: {
  onClear: () => void;
  showIcon?: boolean;
  disabled?: boolean;
  value?: Date;
  clearable?: boolean;
  Popup: PopupHandles<Date | undefined>;
  border?: string;
  placeholder?: ReactNode;
  className?: string;
}) {
  return (
    <button
      disabled={disabled}
      type="button"
      onClick={(e) => {
        e.preventDefault();
        Popup.toggle();
      }}
      ref={Popup.setAnchor}
      className={`form__input flex items-center justify-between ${border} ${className}`}
    >
      {!value && <span className="text-gray-700  ">{placeholder}</span>}

      <USDate className="tracking-widest text-gray-dark " date={value} />

      {(clearable || showIcon || !disabled) && value && (
        <div
          className="flex items-center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {clearable && !disabled && (
            <CancelSelectionButton
              onClick={() => {
                if (!disabled) {
                  onClear();
                  Popup.setContextData(undefined);
                  Popup.close();
                }
              }}
            />
          )}
        </div>
      )}
      {showIcon && !value && <CalendarIcon className="w-5 h-5 text-green" />}
    </button>
  );
}
